// Copyright (c) Bentley Systems

/**
 * useApiExecute
 *
 * CSP REQUIREMENTS (Content Security Policy)
 * -------------------------------------------
 * When this hook (or a module built from it) is deployed, the host application's
 * Content-Security-Policy header **must** allow the Azure SignalR Service domains
 * for both HTTPS REST traffic and WSS (WebSocket) upgrade traffic.
 *
 * Minimum required CSP directives:
 *
 *   connect-src
 *     https://*.service.signalr.net   — initial HTTP negotiate + token exchange
 *     wss://*.service.signalr.net     — persistent WebSocket connection for streaming
 *
 * Example snippet (add to your existing connect-src list):
 *
 *   Content-Security-Policy: connect-src 'self'
 *     https://*.service.signalr.net
 *     wss://*.service.signalr.net
 *     ... other allowed origins ...;
 *
 * Without both entries the browser will block either the negotiate request
 * (HTTPS) or the WebSocket upgrade (WSS), and SignalR will fail silently or
 * with a CORS/CSP console error.
 *
 * TODO: When this hook is extracted into a shared module, move this note to
 *       the module's README / integration guide.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { ConfigManager } from "../Helpers/config-manager";

/** Whether the API execution should open a SignalR connection for streaming results. */
type SignalRMode = "signalr" | "no signalr";

/** Which backend endpoint to target. */
type EndpointMode = "core" | "legacy";

/**
 * Hook for executing API requests in the ApiTesting page.
 *
 * Supports two modes:
 * - "no signalr": POST to /execute, display the JSON response directly.
 * - "signalr": POST to /execute-signalr, receive a SignalR hub URL + token,
 *   then open a real-time connection to stream results back.
 *
 * In SignalR mode `isSending` stays `true` until the connection closes (or the
 * optional `signalRTimeoutMs` fires), keeping the Send button disabled while
 * streaming. Incoming events are appended to `response` so the user sees data
 * as it arrives. After `signalRTimeoutMs` the connection is stopped automatically.
 */
type UseApiExecuteProps = {
  iTwinId: string | undefined;
  selectedProjectUniqueName: string | undefined;
  accessToken: string;
  requestBody: string;
  signalRMode: SignalRMode;
  /** How long (ms) to wait for SignalR results before closing the connection. Default: 120 000 (2 min). */
  signalRTimeoutMs?: number;
};

export type { SignalRMode, EndpointMode };

export const useApiExecute = ({
  iTwinId,
  selectedProjectUniqueName,
  accessToken,
  requestBody,
  signalRMode,
  signalRTimeoutMs = 120_000,
}: UseApiExecuteProps) => {
  const [isSending, setIsSending] = useState(false);
  const [isSignalRConnected, setIsSignalRConnected] = useState(false);
  const [response, setResponse] = useState(" ");
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const [activityId, setActivityId] = useState<string | null>(null);

  const signalRConnectionRef = useRef<HubConnection | null>(null);
  const sendAbortRef = useRef<AbortController | null>(null);
  const signalRTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runIdRef = useRef<string | null>(null);

  // Cleanup SignalR connection and pending timeout on unmount
  useEffect(() => {
    return () => {
      if (signalRTimeoutRef.current) {
        clearTimeout(signalRTimeoutRef.current);
        signalRTimeoutRef.current = null;
      }
      if (signalRConnectionRef.current) {
        void signalRConnectionRef.current.stop();
        signalRConnectionRef.current = null;
      }
    };
  }, []);

  const handleSend = useCallback(async () => {
    if (!iTwinId || !selectedProjectUniqueName) {
      setResponse("Error: Please select an iTwin project and a catalog project.");
      return;
    }

    // Abort any in-flight HTTP request
    if (sendAbortRef.current) {
      sendAbortRef.current.abort();
    }
    sendAbortRef.current = new AbortController();

    // Stop any previous SignalR connection and clear its timeout
    if (signalRTimeoutRef.current) {
      clearTimeout(signalRTimeoutRef.current);
      signalRTimeoutRef.current = null;
    }
    if (signalRConnectionRef.current) {
      await signalRConnectionRef.current.stop();
      signalRConnectionRef.current = null;
    }

    const executeSegment = signalRMode === "signalr" ? "execute-signalr" : "execute";
    const url = `${ConfigManager.edfsCoreItwinInternalUrl}/itwins/${encodeURIComponent(
      iTwinId ?? "",
    )}/packages/${encodeURIComponent(selectedProjectUniqueName ?? "")}/${executeSegment}`;
    const runId = crypto.randomUUID();
    runIdRef.current = runId;

    setIsSending(true);
    setResponse(" ");
    setPendingJobId(null);
    setActivityId(null);

    // In SignalR mode isSending stays true until onclose fires — tracked here
    // so the finally block only clears it in non-signalR / error paths.
    let signalRStarted = false;

    try {
      const fetchResponse = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        body: requestBody,
        signal: sendAbortRef.current.signal,
      });

      setActivityId(fetchResponse.headers.get("activityid"));
      const responseText = await fetchResponse.text();

      // Narrow inner try/catch to only JSON.parse — SignalR errors must reach the outer catch.
      let parsed: any;
      let parseSuccess = false;
      try {
        parsed = JSON.parse(responseText);
        parseSuccess = true;
      } catch {
        setResponse(responseText);
      }

      if (parseSuccess) {
        if (signalRMode === "signalr" && parsed.url && parsed.token) {
          const connection = new HubConnectionBuilder()
            .withUrl(parsed.url, {
              accessTokenFactory: () => parsed.token,
            })
            .withAutomaticReconnect()
            .build();

          // Handle incoming PollResponse: { isSuccess, displayMessage, result, hasLargeResult }
          connection.on("SignalrEvents", (message: unknown) => {
            // SignalR's JSON hub protocol already deserialises the payload, so
            // `message` may arrive as an object rather than a raw string.
            const parsedMessage: unknown = typeof message === "string"
              ? (() => { try { return JSON.parse(message); } catch { return message; } })()
              : message;

            // Format for readable display
            let displayText: string;
            let isTerminal = false;
            const isPollResponse = parsedMessage !== null
              && typeof parsedMessage === "object"
              && "isSuccess" in (parsedMessage as Record<string, unknown>);

            if (isPollResponse) {
              // PollResponse: { isSuccess, displayMessage, result, hasLargeResult }
              const msg = parsedMessage as Record<string, unknown>;
              const isSuccess = msg.isSuccess === true;
              const displayMessage = typeof msg.displayMessage === "string" ? msg.displayMessage : "";
              const hasLargeResult = msg.hasLargeResult === true;
              const hasResult = "result" in msg && msg.result !== null && msg.result !== undefined;
              // Every PollResponse is a terminal event — close connection after displaying
              isTerminal = true;

              // Large result: server stored data in Redis, client must fetch via REST
              if (hasLargeResult && !hasResult && isSuccess) {
                const jobId = runIdRef.current;
                if (jobId && iTwinId && selectedProjectUniqueName) {
                  const fetchUrl = `${ConfigManager.edfsCoreItwinInternalUrl}/itwins/${encodeURIComponent(
                    iTwinId,
                  )}/packages/${encodeURIComponent(
                    selectedProjectUniqueName,
                  )}/execute/${encodeURIComponent(jobId)}`;
                  setResponse("[Large result — fetching from server...]");
                  void (async () => {
                    try {
                      const fetchResp = await fetch(fetchUrl, {
                        method: "GET",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: accessToken,
                          RunId: jobId,
                        },
                      });
                      const text = await fetchResp.text();
                      let resultDisplay: string;
                      try {
                        const jsonResult = JSON.parse(text);
                        resultDisplay = JSON.stringify(jsonResult, null, 2);
                      } catch {
                        resultDisplay = text;
                      }
                      const parts: string[] = [];
                      if (isSuccess) {
                        parts.push("Success (large result fetched)");
                        if (displayMessage) { parts.push(displayMessage); }
                      } else {
                        parts.push(`Error: ${displayMessage || "Execution failed"}`);
                      }
                      parts.push("");
                      parts.push(resultDisplay);
                      setResponse(parts.join("\n"));
                    } catch (err: any) {
                      setResponse(`Error fetching large result: ${err?.message ?? "Request failed"}`);
                    } finally {
                      void connection.stop();
                    }
                  })();
                  return; // Don't stop connection yet — the async fetch will do it
                } else {
                  displayText = `Error: Large result signalled but missing context (jobId/iTwinId/project) to fetch it.`;
                }
              } else {
                const parts: string[] = [];
                if (isSuccess) {
                  parts.push("Success");
                  if (displayMessage) { parts.push(displayMessage); }
                } else {
                  parts.push(`Error: ${displayMessage || "Execution failed"}`);
                }
                if (hasResult) {
                  parts.push("");
                  parts.push(typeof msg.result === "string" ? msg.result : JSON.stringify(msg.result, null, 2));
                }
                displayText = parts.join("\n");
              }
            } else if (typeof parsedMessage === "string") {
              displayText = parsedMessage;
            } else {
              displayText = JSON.stringify(parsedMessage, null, 2);
            }

            const noDataYet = (s: string) => s === " " || s === "Waiting for SignalR response...";
            setResponse((prev) =>
              noDataYet(prev)
                ? displayText
                : `${prev}\n---\n${displayText}`,
            );

            // Close connection only on terminal events (those carrying an actual result)
            if (isTerminal) {
              void connection.stop();
            }
          });

          // onclose: clear timeout, release sending lock, append status
          connection.onclose(() => {
            if (signalRTimeoutRef.current) {
              clearTimeout(signalRTimeoutRef.current);
              signalRTimeoutRef.current = null;
            }
            signalRConnectionRef.current = null;
            setIsSignalRConnected(false);
            setIsSending(false);
            setResponse((prev) =>
              prev === " " || prev === "Waiting for SignalR response..."
                ? "[SignalR connection closed — no data received]"
                : `${prev  }\n\n[SignalR connection closed]`,
            );
          });

          connection.onreconnecting(() => {
            setResponse((prev) => `${prev  }\n\n[SignalR reconnecting...]`);
          });

          connection.onreconnected(() => {
            setResponse((prev) => `${prev  }\n\n[SignalR reconnected]`);
          });

          await connection.start();
          signalRConnectionRef.current = connection;
          signalRStarted = true;
          setIsSignalRConnected(true);
          setResponse("Waiting for SignalR response...");

          // Auto-disconnect after timeout
          signalRTimeoutRef.current = setTimeout(async () => {
            signalRTimeoutRef.current = null;
            if (signalRConnectionRef.current) {
              setResponse((prev) =>
                `${prev  }\n\n[Timeout after ${signalRTimeoutMs / 1_000}s — closing connection]`,
              );
              await signalRConnectionRef.current.stop();
            }
          }, signalRTimeoutMs);
        } else {
          // Non-signalR mode: check for async Processing response
          if (
            parsed.status === "Processing" &&
            parsed.jobId
          ) {
            setPendingJobId(parsed.jobId);
            setResponse(
              `[Processing — job not yet complete]\n\nJob ID: ${parsed.jobId}\n\nUse the "Fetch result" button to poll for the result when ready.${ 
                parsed.pollResult
                  ? `\n\nPoll hint:\n${JSON.stringify(parsed.pollResult, null, 2)}`
                  : ""}`,
            );
          } else {
            setResponse(JSON.stringify(parsed, null, 2));
          }
        }
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setResponse(`Error: ${error?.message ?? "Request failed"}`);
      }
    } finally {
      // SignalR mode keeps isSending=true until onclose — only clear here otherwise
      if (!signalRStarted) {
        setIsSignalRConnected(false);
        setIsSending(false);
      }
    }
  }, [iTwinId, selectedProjectUniqueName, accessToken, requestBody, signalRMode, signalRTimeoutMs]);

  const fetchJobResult = useCallback(async () => {
    if (!pendingJobId || !selectedProjectUniqueName) { return; }

    sendAbortRef.current?.abort();
    sendAbortRef.current = new AbortController();

    const url = `${ConfigManager.edfsCoreItwinInternalUrl}/itwins/${encodeURIComponent(
      iTwinId ?? "",
    )}/packages/${encodeURIComponent(
      selectedProjectUniqueName,
    )}/execute/${encodeURIComponent(pendingJobId)}`;
    const runId = crypto.randomUUID();

    setIsSending(true);
    setResponse(`[Fetching job result for ${pendingJobId}...]`);
    setActivityId(null);

    try {
      const fetchResponse = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        signal: sendAbortRef.current.signal,
      });
      setActivityId(fetchResponse.headers.get("activityid"));
      const text = await fetchResponse.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed.status === "Processing" && parsed.jobId) {
          setResponse(
            `[Still processing — job not yet complete]\n\nJob ID: ${parsed.jobId}${ 
              parsed.pollResult
                ? `\n\nPoll hint:\n${JSON.stringify(parsed.pollResult, null, 2)}`
                : ""}`,
          );
        } else {
          setResponse(JSON.stringify(parsed, null, 2));
        }
      } catch {
        setResponse(text);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setResponse(`Error: ${error?.message ?? "Request failed"}`);
      }
    } finally {
      setIsSending(false);
    }
  }, [pendingJobId, iTwinId, selectedProjectUniqueName, accessToken]);

  const handleSendLegacy = useCallback(async () => {
    if (!iTwinId || !selectedProjectUniqueName) {
      setResponse("Error: Please select an iTwin project and a catalog project.");
      return;
    }

    if (sendAbortRef.current) {
      sendAbortRef.current.abort();
    }
    sendAbortRef.current = new AbortController();

    const url = `${ConfigManager.edfsLegacyUrl}/itwins/${iTwinId}/packages/${selectedProjectUniqueName}`;
    const runId = crypto.randomUUID();

    setIsSending(true);
    setResponse(" ");
    setPendingJobId(null);
    setActivityId(null);

    try {
      const fetchResponse = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        body: requestBody,
        signal: sendAbortRef.current.signal,
      });

      setActivityId(fetchResponse.headers.get("activityid"));
      const responseText = await fetchResponse.text();
      if (!fetchResponse.ok) {
        setResponse(`Error: ${fetchResponse.status} ${fetchResponse.statusText}${responseText ? `\n\n${responseText}` : ""}`);
        return;
      }
      try {
        const parsed = JSON.parse(responseText);
        setResponse(JSON.stringify(parsed, null, 2));
      } catch {
        setResponse(responseText);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setResponse(`Error: ${error?.message ?? "Request failed"}`);
      }
    } finally {
      setIsSending(false);
    }
  }, [iTwinId, selectedProjectUniqueName, accessToken, requestBody]);

  return {
    isSending,
    isSignalRConnected,
    response,
    activityId,
    handleSend,
    handleSendLegacy,
    pendingJobId,
    fetchJobResult,
  };
};
